import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  asArray, ComponentPrototype,
  digOut,
  isNull,
  MusimapCollection,
} from '../../../../search/musimap-filters';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, timer } from 'rxjs';
import { getUID } from '../utils/helpers';
import {
  IAttachmentDTO,
  IAttachmentsFormItem,
  IAttachmentsFormItemDetails,
  IAttachmentUploadResult,
} from '../utils/interfaces';
import { AttachmentsFormTypeSelectorComponent } from '../../misc/attachments-form-type-selector.component';
import { EAttachmentsCategory } from '../../constants/attachments-category.enum';
import { EAttachmentType, EAttachmentUploadResults } from '../utils/enums';
import { take, tap } from 'rxjs/operators';
import { htmlToText } from 'src/app/project/pipes/helpers';

const DEFAULT_TITLE = 'Add Attachment(s)';
const UID_KEY = 'id';

@Component({
  selector: 'app-attachments-form',
  templateUrl: '../templates/attachments-form.component.html',
  styleUrls: ['../styles/attachments-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsFormComponent extends ComponentPrototype implements OnInit {
  @Input() public title: string = DEFAULT_TITLE;

  @Input() public placement: string = 'left';

  @Input() public showDefaultType: boolean = false;

  @Input() public category: EAttachmentsCategory = EAttachmentsCategory.ATTACHMENTS;

  @Output() public ready: EventEmitter<this> = new EventEmitter<this>();

  @Output() public submit: EventEmitter<IAttachmentDTO[]> = new EventEmitter<IAttachmentDTO[]>();

  @ViewChild('attachmentsForm') public attachmentsForm: NgbPopover;

  public categorySelector: AttachmentsFormTypeSelectorComponent;

  public defaultType: EAttachmentType = EAttachmentType.GENERAL_PROJECT_ATTACHMENT;

  public uploadResultsTypes: typeof EAttachmentUploadResults = EAttachmentUploadResults;

  public isOpened$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public isSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public uploadResults$: BehaviorSubject<IAttachmentUploadResult[]> = new BehaviorSubject<IAttachmentUploadResult[]>(
    null
  );

  public readonly files: MusimapCollection<IAttachmentsFormItem> = new MusimapCollection<IAttachmentsFormItem>({
    indexBy: UID_KEY,
  });

  public open = (): this => {
    this.attachmentsForm?.open();
    return this;
  };

  public close = (): this => {
    this.attachmentsForm?.close();
    return this;
  };

  public toggleSaving = (state: boolean | null = null): this => {
    this.isSaving$.next(isNull(state) ? !this.isSaving$.value : !!state);
    return this;
  };

  public addFiles = (data: File | File[]): this => {
    this.files.add(
      asArray(data).map(file => ({
        id: getUID(),
        file,
        name: file.name,
      }))
    );

    return this;
  };

  public updateFileDetails = (file: IAttachmentsFormItem, updateQuery: IAttachmentsFormItemDetails): void => {
    Object.keys(updateQuery).forEach(key => (file[key] = updateQuery[key] || ''));
  };

  public submitForm = (): void => {
    const label = this.categorySelector?.getSelected();
    const { category } = this;
    label &&
      this.files.size &&
      this.submit.emit(
        this.files.map<IAttachmentDTO>((d: IAttachmentsFormItem) => ({
          file: d.file,
          title: d.title || '',
          description: d.description ? htmlToText(d.description) : '',
          category,
          label,
        }))
      );
  };

  public setResults = (uploadResults: IAttachmentUploadResult | IAttachmentUploadResult[]): this => {
    this.uploadResults$.next(
      asArray<IAttachmentUploadResult>(uploadResults).map((d: IAttachmentUploadResult) => {
        d.message = d.message || digOut(d, 'massage');
        return {
          ...d,
          failed: d.message !== EAttachmentUploadResults.SUCCESS,
        };
      })
    );
    timer(1000)
      .pipe(
        take(1),
        tap(() => {
          this.uploadResults$.next(null);
          this.attachmentsForm.close();
        })
      )
      .subscribe();

    return this;
  };

  public dropResults = (): this => {
    this.uploadResults$.next(null);
    return this;
  };

  ngOnInit(): void {
    this.registerSubscriptions(this.isOpened$.pipe(tap(() => this.dropResults())).subscribe());

    this.ready.emit(this);
  }
}
